import { IconForms, IconPlus } from "@tabler/icons-react";
import { Link } from "react-router";
import { ErrorState, LoadingState } from "@how/ui";
import { formFields, type FormRow } from "../form-queries";
import { websiteHref } from "../website-variant";

/**
 * The forms as cards (studio design): what each is for, how many questions it asks, what has arrived and what nobody
 * has read yet, and whether it is taking messages; a card to add one at the end.
 */
export function FormCards({
  forms,
  isError,
  onRetry,
  onAdd,
}: {
  readonly forms: readonly FormRow[] | undefined;
  readonly isError: boolean;
  readonly onRetry: () => void;
  readonly onAdd: (() => void) | null;
}) {
  if (isError) return <ErrorState message="Could not load forms." onRetry={onRetry} />;
  if (forms === undefined) return <LoadingState rows={3} label="Loading your forms" />;
  const unread = forms.reduce((sum, f) => sum + f.unread, 0);
  const received = forms.reduce((sum, f) => sum + f.submissions, 0);
  return (
    <>
      <ul className="ws-stats" aria-label="Forms at a glance">
        <li>
          <strong>{forms.length}</strong> {forms.length === 1 ? "form" : "forms"}
        </li>
        <li>
          <strong>{received}</strong> received
        </li>
        <li data-state={unread > 0 ? "changed" : "published"}>
          <span className="ws-dot" aria-hidden />
          <strong>{unread}</strong> unread
        </li>
      </ul>
      <ul className="ws-forms" aria-label="Forms">
        {forms.map((form) => (
          <li key={form.id}>
            <Link to={websiteHref("studio", `/website/forms/${form.id}`)} className="ws-form">
              <span className="ws-form__head">
                <span className="ws-form__icon" aria-hidden>
                  <IconForms size={20} stroke={1.8} />
                </span>
                <span className="ws-form__titles">
                  <span className="ws-form__name">{form.name}</span>
                  <span
                    className="ws-card__status"
                    data-state={form.is_active ? "published" : "draft"}
                  >
                    <span className="ws-dot" aria-hidden />
                    {form.is_active ? "Taking messages" : "Closed"}
                  </span>
                </span>
                {form.unread > 0 ? (
                  <span className="ws-form__unread">{form.unread} unread</span>
                ) : null}
              </span>
              {form.description === null ? null : (
                <span className="ws-form__description">{form.description}</span>
              )}
              <span className="ws-form__figures">
                <span>
                  <strong>{formFields(form.fields).length}</strong>
                  questions
                </span>
                <span>
                  <strong>{form.submissions}</strong>
                  received
                </span>
                <span>
                  <strong>{form.unread}</strong>
                  unread
                </span>
              </span>
            </Link>
          </li>
        ))}
        {onAdd === null ? null : (
          <li>
            <button type="button" className="ws-card ws-card--add" onClick={onAdd}>
              <span className="ws-card--add__icon" aria-hidden>
                <IconPlus size={22} />
              </span>
              Add a form
            </button>
          </li>
        )}
      </ul>
      {forms.length === 0 ? (
        <p className="ws-note">
          A contact form lets visitors write to the church without publishing an email address.
        </p>
      ) : null}
    </>
  );
}
